import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import Bong from '~Scenes/POS';

import { useSelector, useDispatch } from '~/Hooks';

import * as actions from '~actions';
import OrderIdManager from '~Components/OrderIdManager';
import { useStorage } from '~/Components/Storage';
import { useInventoryUniverse, useInventoryApi } from '~Components/InventoryUniverse';
import { generateID } from '~reducers';
import { useCart } from '~Components/CartProvider';
import { useCompetition } from '~Providers/CompetitionsProvider';
import useModal from '~ComponentsImproved/Modal/useModal';
import QsrPayButtons from '~Scenes/QSR/Components/QSRPayButtons';

const Wrapper = styled.div<{
  active: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--colorBlueGray1100);
  z-index: 1;
  perspective: 1200px;
  transform-style: preserve-3d;
  pointer-events: none;
  opacity: 0;

  ${({ active }) =>
    active &&
    css`
      opacity: 1;
      pointer-events: auto;
    `}
`;

interface Props {
  active: boolean;
  id: number;
  isFirst: boolean;
  onOrderIdChanged: (id: number, orderId: string | null) => void;
  onCompleted: (id: number) => void;
  onPaymentInitiated: (id: number) => void;
  initialEasyId: string | null;
  onEasyIdChanged: (easyId: string | null) => void;
}

const Instance: React.FunctionComponent<Props> = ({
  onOrderIdChanged,
  onCompleted,
  onPaymentInitiated,
  active,
  id,
  isFirst,
  initialEasyId,
  onEasyIdChanged,
}) => {
  const cart = useCart();
  const dispatch = useDispatch();
  const history = useHistory();
  const competitions = useCompetition();
  const splitPaymentModal = useModal();

  const [, , venueId] = location.hash.split('/');
  const [paymentMethod, setPaymentMethod] = useState<{
    id: string;
    poiid?: string;
  }>();
  const [orderId, setOrderId] = useState<string>('');

  const [bongReactKey, setBongReactKey] = useState(0);
  const [orderWasBonged, setOrderWasBonged] = useState<boolean>(false);
  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);
  const staffId = useSelector((state) => state.staff?.id);
  const [storage] = useStorage();

  const splitOrder = useSelector((state) => state.POSSplitOrder);

  // After we have started a payment process, this order will be populated
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));

  // After the order has been created and the items have been added,
  // a payment is created and is returned here
  const payment = useSelector(
    (state) => order && state.POSPayments.find((p) => p.order === order.id),
  );

  // On page boot, set the order ID
  const reloadOrderId = () => setOrderId(generateID());
  useEffect(() => {
    reloadOrderId();
  }, []);

  useEffect(() => {
    onOrderIdChanged(id, orderId);
  }, [orderId]);

  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);

  // When collecting, initialize easyId from parent so order number persists across tabs
  useEffect(() => {
    if (bongPrintingHalted && initialEasyId) {
      cart.setEasyId(initialEasyId);
    }
  }, []);

  // Report easyId changes to parent so new tabs get the same order number when collecting
  useEffect(() => {
    if (bongPrintingHalted && cart.easyId) {
      onEasyIdChanged(cart.easyId);
    }
  }, [cart.easyId, bongPrintingHalted]);

  useEffect(() => {
    if (!bongPrintingHalted) {
      cart.setEasyId(null);
    }
  }, [bongPrintingHalted]);

  const [enableQuantityRefetch, setEnableQuantityRefetch] = useState<boolean>(true);

  const itemsWithQuantity = useSelector((state) => state.POSItemsIds);

  const basketItemsWithQuantity: Array<{
    id: string;
    quantity: number;
  }> = [];

  cart.items.forEach((currentItem) => {
    const exist = basketItemsWithQuantity.find(
      (basketItem) => basketItem.id === currentItem.itemId,
    );
    if (!exist) {
      const currentItems = cart.items.filter((item) => item.itemId === currentItem.itemId);
      basketItemsWithQuantity.push({ id: currentItem.itemId, quantity: currentItems?.length });
    }
  });

  const leftItems: Array<{
    id: string;
    quantity: number;
    outOfStock: boolean;
  }> = [];

  itemsWithQuantity.forEach((currentItem) => {
    const basketItem = basketItemsWithQuantity.find(
      (basketItemWithQuantity) => basketItemWithQuantity.id === currentItem.id,
    );

    if (basketItem) {
      leftItems.push({
        ...currentItem,
        quantity: currentItem.quantity - basketItem.quantity,
      });
    } else {
      leftItems.push(currentItem);
    }
  });

  const handleEnableQuantityRefetch = (value: boolean) => {
    setEnableQuantityRefetch(value);
  };

  // Dont animate the highlight on page boot, but start showing it
  // after a short timeout that hides the boot animation
  // useEffect(() => setTimeout(() => setShowHighlight(true), 1000), []);

  // When the order has been created, add items to the order and
  // create a payment right away
  useEffect(() => {
    if (!order || !paymentMethod?.id) {
      return;
    }

    cart.packagedItems.forEach((item) => {
      const orderItem = {
        ...item,
        fraction: item.fraction ?? 1,
        count: item.count ?? 1,
        amount: item.amount ?? 0,
        entryId: item.entryId ?? '',
        discount: item.discount ?? 0,
      };
      dispatch(actions.addItemToOrder(order.id, orderItem));
    });

    if (!splitOrder) {
      const variant =
        paymentMethod.id === 'ADYEN'
          ? paymentMethod.poiid ?? storage.adyenTerminals[0]?.POIID
          : undefined;

      dispatch({
        type: 'POS_PAYMENT_REQUEST',
        method: paymentMethod.id,
        variant,
        order: orderId,
        // Carries the acquired card through to the Adyen component, so it
        // charges the card the shopper already presented.
        meta: cart.cardIdentity ? { cardIdentity: cart.cardIdentity } : undefined,
      });

      /**
       * If we are starting a credit card payment,
       * automatically open a new tab while the customer is paying.
       * We add a little delay as to not confuse the staff
       */
      if (['IZETTLE', 'ADYEN'].includes(paymentMethod.id!)) {
        setTimeout(() => {
          onPaymentInitiated(id);
        }, 500);
      }
    }
  }, [order && order.id]);
  /*
   * Handle payment status updates
   */
  useEffect(() => {
    if (!payment) {
      return;
    }

    // Don't reset if splitting order payment
    if (splitOrder) {
      return;
    }

    if (payment.status === 'COMPLETED') {
      const items = _.groupBy(cart.packagedItems, (item) => item.itemId);

      Object.keys(items).forEach((key) => {
        const inInventory = inventoryUniverse.items.find((uniItem) => uniItem.itemId === key);
        if (!inInventory || !inInventory.count) return;

        const nrOfSameItem = items[key].length;
        inventoryApi.reduceStock(key, nrOfSameItem, 'Order');
      }, []);

      if (staffId) {
        competitions.addSale({
          staffId: staffId,
          items: cart.packagedItems,
          tip: payment.meta.tip,
        });
      }

      handleEnableQuantityRefetch(false);
      setTimeout(() => {
        dispatch(actions.setPOSItemsIds(leftItems));

        onCompleted(id);
      }, 1000);
    }

    if (payment.status === 'FAILED') {
      setTimeout(() => {
        dispatch({
          type: 'REMOVE_POS_PAYMENT',
          id: payment.id,
        });

        dispatch({
          type: 'REMOVE_POS_ORDER',
          id: orderId,
        });

        reloadOrderId();
      }, 1000);
    }
  }, [payment && payment.status]);

  function reset() {
    splitPaymentModal.hide();
    setBongReactKey(bongReactKey + 1);
    // setOrderWasBonged(false); //TODO: fix
    cart.setAppliedDiscount(null);
    cart.setItems([]);
    // Otherwise the member and their card follow this tab into the next order,
    // and the payment would reuse an acquisition the terminal has let go of.
    cart.clearLoyalty();
    reloadOrderId();
    cart.updateServiceType(storage.serviceType);

    if (!bongPrintingHalted) {
      cart.setEasyId(null);
    }

    onCompleted(id);
  }

  return (
    <Wrapper active={active}>
      <Bong
        yOffset={80}
        paymentId={payment ? payment.id : null}
        key={bongReactKey}
        venueId={venueId}
        serviceType={cart.serviceType}
        orderId={orderId}
        buttons={
          <QsrPayButtons
            venueId={venueId}
            orderId={orderId}
            onComplete={reset}
            setPaymentMethod={(id: string, poiid?: string) => {
              setPaymentMethod({ id, poiid });
            }}
            orderWasBonged={orderWasBonged}
          />
        }
        onClose={() => history.push('gateway')}
        leftItems={leftItems}
        enableQuantityRefetch={enableQuantityRefetch}
        onEnableQuantityRefetch={handleEnableQuantityRefetch}
        reset={reset}
        isActive={active}
        setOrderWasBonged={setOrderWasBonged}
      />

      {orderId && (
        <OrderIdManager
          reset={() => {}}
          venueId={venueId}
          orderId={orderId}
          visible={false}
          setOrderEasyId={cart.setEasyId}
          orderEasyId={cart.easyId}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(Instance);
